import { ServerMessagePayload } from '@common/api';
import { Container, Text } from 'pixi.js';

import { ClientRegistry } from '../components';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

interface NameTextData {
  text: Text;
}

/**
 * Module-level cache of name text objects per entity.
 * Stored outside the class to persist across system recreation (e.g., reconnection).
 */
const nameTexts = new Map<number, NameTextData>();

export class NameRenderSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['DisplayName', 'VisualPosition'];
  private namesContainer: Container | null = null;

  setNamesContainer(container: Container) {
    this.namesContainer = container;
  }

  updateEntity({
    world,
    entityId,
  }: FilteredClientComponentSystemUpdateEntityArgs): ServerMessagePayload[] {
    if (!this.namesContainer) {
      return [];
    }

    const displayName = world.getComponent(entityId, 'DisplayName')!;
    const pos = world.getComponent(entityId, 'VisualPosition')!;

    let nameData = nameTexts.get(entityId);

    if (!nameData) {
      // Create new text for the name
      const text = new Text({
        text: displayName.name,
        style: {
          fontFamily: 'monospace',
          fontSize: 12,
          fill: 0xffffff,
          stroke: { color: 0xcccccc, width: 1 },
          align: 'center',
          letterSpacing: 1,
          dropShadow: {
            color: '#000000',
            alpha: 1,
            blur: 5,
            distance: 2,
          },
        },
      });
      text.anchor.set(0.5, 0);
      this.namesContainer.addChild(text);

      nameData = { text };
      nameTexts.set(entityId, nameData);

      // Register cleanup callback for when entity is destroyed
      world.registerOnEntityDestroyed(entityId, (id) => this.cleanupEntity(id));
    }

    // Update text content if name changed
    if (nameData.text.text !== displayName.name) {
      nameData.text.text = displayName.name;
    }

    // Counter-scale the text so it renders at screen size (not zoomed)
    // TODO: Get zoom from state.data instead of hardcoding
    const zoom = 6;
    nameData.text.scale.set(1 / zoom);
    nameData.text.position.set(pos.x, pos.y);

    return [];
  }

  private cleanupEntity(entityId: number) {
    const nameData = nameTexts.get(entityId);
    if (nameData) {
      nameData.text.destroy();
      nameTexts.delete(entityId);
    }
  }
}
